<template>
  <section
    class="foyerCategory"
    :style="{
      backgroundImage: activityData.images.bg ? `url(${activityData.images.bg})` : 'none',
      '--card-bgColor': card.bgColor,
      '--card-borderRadius': card.borderRadius,
      '--card-subColor': card.subColor,
      '--card-textColor': card.textColor,
      '--card-titleColor': card.titleColor,
      '--card-buttonBorderRadius': card.buttonBorderRadius,
      '--card-buttonTextColor': card.buttonTextColor,
      '--card-buttonBgColor': card.buttonBgColor,
      '--card-buttonOutlineColor': card.buttonOutlineColor,
      '--card-scrollbarBgColor': card.scrollbarBgColor,
      '--card-scrollbarBorderRadius': card.scrollbarBorderRadius,
      '--card-scrollbarHandleColor': card.scrollbarHandleColor,
      '--card-descriptionFontSize': card.descriptionFontSize,
      '--card-titleFontSize': card.titleFontSize,
      '--card-roleFontSize': card.roleFontSize,
      '--card-buttonFontSize': card.buttonFontSize,
    }">
    <Header :notificationIconClass="`fa-calendar-check`" :playProfileNotification="playProfileNotification" v-if="!hideHeader" />
    <iframe
      style="position: fixed; top: 0; bottom: 0; left: 0; height: 100dvh; width: 100vw; z-index: 0"
      :src="activityData.property.srcVideo"
      frameborder="0"
      v-if="activityData.property.srcVideo ? activityData.property.srcVideo.startsWith('https://') : false"></iframe>
    <div class="foyerCategory-container">
      <Banner v-if="$store.state.template.banners.enableBanners" :bannerSlot="'cards_top_banner'" />
      <div class="foyerCategory-title" v-html="title"></div>

      <button class="cards-manager-toggle" @click="showCardsManager = !showCardsManager" :disabled="isLoading" v-if="isUserAdmin">
        {{ isLoading ? $t('foyerCategory.buttons.saving') : $t('foyerCategory.buttons.edit') }}
      </button>

      <div class="foyerCategory-card-search-container" v-if="enableCardSearch">
        <img src="@/assets/img/search.svg" />
        <input type="text" style="pointer-events: auto" v-model="search" :placeholder="computedSearchPlaceholder" />
      </div>
      <div class="conferenceCards-container" :class="{ 'foyerCategory-grided-container': activityData.property.foyerCategoryUsesNewCard }" style="pointer-events: auto">
        <ConferenceCard
          v-for="(card, i) in cardsWithTranslatedActions"
          :key="i"
          :conferenceCard="card"
          @finished="loadActivity"
          @playProfileNotification="playProfileNotification = $event"
          v-if="!activityData.property.foyerCategoryUsesNewCard" />
        <NewCard v-for="(card, i) in cardsWithTranslatedActions" :key="i" :newCard="card" @finished="loadActivity" @playProfileNotification="playProfileNotification = $event" v-if="activityData.property.foyerCategoryUsesNewCard" />
      </div>
      <CardsManager
        :category="activityData.property.category"
        :cards="cardsWithTranslatedActions"
        @save="saveCard"
        @close="showCardsManager = false"
        @update="loadActivity"
        @delete-card="deleteCard($event)"
        v-if="showCardsManager"
        @reorder:cards="reorderCards($event)" />
      <div class="loading-overlay" v-if="isLoading">
        <div class="loading-spinner"></div>
        <p>{{ $t('foyerCategory.processing') }}</p>
      </div>
    </div>
    <div class="footer-wrapper">
      <Footer />
    </div>
  </section>
</template>

<script>
import Header from '@/components/general/Header.vue';
import ConferenceCard from '@/components/general/ConferenceCard.vue';
import NewCard from '@/components/general/NewCard.vue';
import Banner from '@/components/banners/Banner.vue';
import Footer from '@/components/general/Footer.vue';
import api from '@/services/api.js';
import CardsManager from '../components/cards/CardsManager.vue';

export default {
  name: 'FoyerCategory',
  components: { Header, ConferenceCard, NewCard, Banner, Footer, CardsManager },

  data() {
    return {
      isUserAdmin: false,
      card: null,
      hideHeader: false,
      playProfileNotification: false,
      enableCardSearch: false,
      enablePagination: true,
      searchPlaceholder: '',
      search: '',
      activityData: {
        activity: '',
        images: {},
        speaker: [],
        interactivities: {
          chat: false,
          qa: false,
          quiz: false,
          poll: false,
        },
        property: {
          config: '',
          category: '',
        },
      },
      title: '',
      page: [],
      itensPerPage: 12,
      paginationKey: 0,
      customLabels: {
        first: '⪡',
        last: '⪢',
        previous: '<',
        next: '>',
      },
      customStyles: {
        a: {
          color: 'var(--card-buttonTextColor)',
          backgroundColor: 'var(--card-buttonBgColor)',
          borderRadius: '50%',
        },
      },
      cards: [],
      showCardsManager: false,
      isLoading: false,
    };
  },
  computed: {
    filteredCards() {
      return this.cards.filter((card) => {
        try {
          let filteredByText = card.name.toLowerCase().match(this.search.toLowerCase()) || card.role.toLowerCase().match(this.search.toLowerCase()) || card.description.toLowerCase().match(this.search.toLowerCase());
          return filteredByText;
        } catch (err) {
          console.error(err);
          return false;
        }
      });
    },
    computedSearchPlaceholder() {
      return this.searchPlaceholder || this.$t('foyerCategory.searchPlaceholder');
    },
    cardsWithTranslatedActions() {
      return this.filteredCards.map((card) => ({
        ...card,
        action: this.getTranslatedAction(card),
      }));
    },
  },
  methods: {
    getTranslatedAction(card) {
      // If there's a custom action, use it directly
      if (card.customAction) {
        return card.customAction;
      }

      // If there's an actionKey, translate it
      if (card.actionKey) {
        if (card.actionParams && Object.keys(card.actionParams).length > 0) {
          return this.$t(card.actionKey, card.actionParams);
        } else {
          return this.$t(card.actionKey);
        }
      }

      // Fallback to empty string
      return '';
    },
    onChangePage(page) {
      this.page = page;
    },
    async loadActivity(isReload = false) {
      if (!isReload) {
        this.cards = [];
      }

      try {
        await this.fetchAndProcessActivityData();
        await this.fetchAndProcessCards(isReload);
      } catch (error) {
        console.error('Failed to load activity data:', error);
      }
    },
    async fetchAndProcessActivityData() {
      try {
        const response = await api.get(`activity/${this.$route.params.stream}`);
        if (!response.data) return;

        let tempImages = { ...this.activityData.images };
        this.activityData = { ...this.activityData, ...response.data };
        this.activityData.images = response.data.images ? JSON.parse(response.data.images) : tempImages;

        let tempProperty = { ...this.activityData.property };
        this.activityData.property = response.data.property ? JSON.parse(response.data.property) : tempProperty;

        this.itensPerPage = this.activityData.property.cardsPerPage ? this.activityData.property.cardsPerPage : this.itensPerPage;

        response.data.interactivity.forEach((item) => {
          this.activityData.interactivities[item.interactivity] = item.enable;
        });

        this.title = this.activityData.property.sectionTitle ? this.activityData.property.sectionTitle : '';
        this.enableCardSearch = this.activityData.property.enableCardSearch ? true : false;
        this.searchPlaceholder = this.activityData.property.searchPlaceholder || '';
        this.hideHeader = this.activityData.property.hideHeader ? true : false;

        return this.activityData;
      } catch (error) {
        console.error('Error fetching activity data:', error);
        throw error;
      }
    },
    async fetchAndProcessCards(isReload) {
      try {
        const category = this.activityData.property.category;
        if (!category) {
          return;
        }

        const response = await api.get(`/event/${this.$store.state.eventId}/foyer?category=${category}`);

        const newCards = [];
        response.data.forEach((item) => {
          const cardData = this.processCardItem(item);
          if (cardData && !isReload) {
            newCards.push(cardData);
          }
        });

        if (!isReload) {
          this.cards = [...newCards];
          this.cards.sort((a, b) => (a.order > b.order ? 1 : -1));
          this.enablePagination = this.itensPerPage < this.cards.length;
        }

        return this.cards;
      } catch (error) {
        console.error('Error fetching cards:', error);
        throw error;
      }
    },
    processCardItem(item) {
      try {
        if (item.property) {
          item.property = JSON.parse(item.property);
        }
      } catch (error) {
        console.error('Error parsing property:', error);
      }

      const onair = false;

      const { actionKey, actionType, url, target, panel, enable, actionParams, customAction } = this.determineActionForCard(item, item.property);

      let image = {};
      try {
        image = JSON.parse(item.images);
      } catch (error) {
        console.error('Error parsing image data:', error);
      }

      return {
        image: image.poster,
        name: item.activity,
        order: parseInt(item.order),
        role: item.description_short,
        description: item.description,
        actionType,
        actionKey, // Store translation key instead of translated text
        actionParams: actionParams || {}, // Store parameters for translation
        customAction, // Store custom action text
        enableAction: enable,
        url,
        target,
        panel,
        onair,
        start_date: item.date_start,
        finish_date: item.date_finish,
        activity: item,
      };
    },
    determineActionForCard(item, property) {
      let actionKey,
        actionType,
        target,
        enable = true,
        url,
        panel,
        actionParams = {};

      if (!item.inscription) {
        if (item.activity_type_id == '7dba0918-7cb1-11ea-bc55-0242ac130003') {
          // Live
          actionType = 'router';
          actionKey = 'foyerCategory.buttons.enter';
          url = 'Live';
          target = item.activity_id;
        } else if (item.activity_type_id == '7148a87e-7cb1-11ea-bc55-0242ac130003') {
          // Video Gallery
          actionType = 'router';
          actionKey = 'foyerCategory.buttons.enter';
          url = 'VideoGallery';
          target = item.activity_id;
        } else {
          if (property.meetLink) {
            var parsed = {};
            try {
              parsed = typeof property.meetLink === 'string' ? JSON.parse(property.meetLink) : property.meetLink;

              if (parsed.params && parsed.params.panel) {
                // Explorer
                //console.log('Parsed panel:', { parsed });
                actionType = 'router';
                actionKey = 'foyerCategory.buttons.enter';
                url = 'Explorer';
                target = parsed.params ? parsed.params.stream : '';
                panel = parsed.params ? parsed.params.panel : 'map';
              } else {
                // Everything else
                actionType = 'router';
                actionKey = 'foyerCategory.buttons.enter';
                url = parsed.name;
                target = parsed.params ? parsed.params.stream : '';
              }
            } catch (error) {
              // console.error(error);
              if (typeof property.meetLink === 'string' && property.meetLink.startsWith('http')) {
                actionType = 'link';
                actionKey = 'foyerCategory.buttons.visit';
                url = property.meetLink;
                target = '';
              } else {
                parsed = { name: '', params: { stream: '' } };
              }
            }
          } else {
            actionType = 'link';
            actionKey = property.action ? null : 'foyerCategory.buttons.enter'; // Use custom action if provided
            url = property.meetLink;
            target = item.activity_id;
          }
        }
      } else {
        if (item.inscripted) {
          enable = true;
          if (item.activity_type_id == '6e2aec24-7cb1-11ea-bc55-0242ac130003' || item.activity_type_id == '7dba0918-7cb1-11ea-bc55-0242ac130003') {
            actionType = 'router';
            actionKey = 'foyerCategory.buttons.watch';
            url = 'Live';
            target = item.activity_id;
          } else if (item.activity_type_id == 'f82a83d3-aa7c-4655-9244-4ec745bcf8d5') {
            actionType = 'router';
            actionKey = 'foyerCategory.buttons.followQueue';
            url = 'QueueUser';
            target = item.activity_id;
          } else {
            actionType = 'link';
            actionKey = property.action ? null : 'foyerCategory.buttons.enter'; // Use custom action if provided
            url = property.meetLink;
            target = item.activity_id;
          }
        } else {
          if (!item.inscription_seats) {
            actionKey = 'foyerCategory.buttons.subscribe';
            actionType = 'inscript';
            url = '';
            target = item.activity_id;
            enable = true;
          } else if (item.inscription_left > 0) {
            actionKey = 'foyerCategory.buttons.subscribeRemaining';
            actionParams = { count: item.inscription_left };
            actionType = 'inscript';
            url = '';
            target = item.activity_id;
            enable = true;
          } else {
            actionKey = 'foyerCategory.buttons.soldOut';
            actionType = 'none';
            url = '';
            target = item.activity_id;
            enable = false;
          }
        }
      }

      return {
        actionKey,
        actionType,
        url,
        target,
        panel,
        enable,
        actionParams,
        customAction: property.action, // Store custom action separately
      };
    },
    async reorderCards(cards) {
      this.cards = cards;
      this.cards.forEach(async (card, index) => {
        card.activity.order = index;
        await this.saveCard(card);
      });
    },
    async saveCard(card) {
      try {
        this.isLoading = true;

        const payload = {
          order: card.activity.order,
          activity: card.name,
          description_short: card.name,
          description: card.description,
          images: JSON.stringify({ poster: card.image }),
          property: JSON.stringify(card.activity.property),
        };

        const response = await api.put(`event/${this.$store.state.eventId}/activity/${card.activity.activity_id}`, payload);

        await this.loadActivity();
        this.isLoading = false;
      } catch (error) {
        console.error('Error saving card:', error);
        this.isLoading = false;
      }
    },
    async deleteCard(id) {
      this.$modal.show('dialog', {
        title: this.$t('foyerCategory.dialog.attention'),
        text: this.$t('foyerCategory.dialog.deleteCard'),
        buttons: [
          {
            title: this.$t('foyerCategory.dialog.yes'),
            handler: async () => {
              try {
                this.isLoading = true;

                await api.delete(`event/${this.$store.state.eventId}/activity/${id}`);

                await this.loadActivity();
                this.isLoading = false;

                this.$modal.hide('dialog');
              } catch (error) {
                console.error('Error deleting card:', error);
                this.isLoading = false;
                this.$modal.hide('dialog');
              }
            },
          },
          {
            title: this.$t('foyerCategory.dialog.no'),
            handler: () => {
              this.$modal.hide('dialog');
            },
          },
        ],
      });
    },
  },
  watch: {
    $route() {
      this.cards = [];
      this.loadActivity();
    },
  },
  beforeMount() {
    this.isUserAdmin = this.$store.state.user.role === '1819bd5e-7b46-11ea-bc55-0242ac130003' || this.$store.state.user.role === '1819bea8-7b46-11ea-bc55-0242ac130003'; //operators and admins
    this.card = this.$store.state.template.foyerCard;
    this.loadActivity();
  },
};
</script>

<style lang="scss">
.foyerCategory {
  position: fixed;
  inset: 0;
  background-color: var(--main-color);
  background-position: center;
  background-size: cover;
  background-attachment: fixed;
  background-repeat: no-repeat;
  height: 100%;
  user-select: none;
}
.foyerCategory-container {
  position: relative;
  width: 100%;
  padding: 64px 0;
  height: 100%;
  max-height: calc(100dvh - 32px);

  display: flex;
  flex-direction: column;
  align-content: center;
  align-items: center;

  overflow-y: auto;
  overflow-x: hidden;

  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch; /* enables momentum/rubber band on iOS */
  overscroll-behavior: contain; /* or 'auto' */

  &::-webkit-scrollbar {
    width: 12px;
    @include media('<=tablet') {
      width: 0;
    }
  }
  &::-webkit-scrollbar-track {
    background-color: rgb(70, 69, 66);
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgb(102, 102, 102) !important;
    border-radius: 4px;
    background-image: none !important;
  }

  @include media('<=phone') {
    padding: 10px;
    margin: 30px 0;
    max-height: calc(100dvh - 62px);
    padding-bottom: 40px;
  }
}

.foyerCategory-title {
  margin-top: 1.5%;
  font-size: 3rem;
  text-align: center;
}

.cards-manager-toggle {
  position: absolute;
  top: 68px;
  right: 10px;
  padding: 0.3rem 0.7rem;
  z-index: 10;

  font-size: 0.6rem;
  opacity: 0.5;
  color: white;
  background-color: #494949;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease-in-out;

  @include media('<=tablet') {
    top: 50px;
  }
  @include media('<=phone') {
    position: fixed;
    top: 42px;
    right: 4px;
    margin: 0;
    padding: 0.5rem;
  }
}

.foyerCategory-card-search-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 500px;
  height: 40px;
  margin-top: 1.5%;
  margin-bottom: 1.5%;
  background-color: var(--input-bg);
  border-radius: var(--input-borderRadius);
  border: 1px solid var(--input-border);
  outline: none;
  img {
    margin: auto;
  }
  input {
    width: 86%;
    text-align: left;
    color: var(--input-text);
    background-color: transparent;
    border-radius: var(--input-borderRadius);
    border: 0;
    outline: none;
    margin-left: 2%;

    &::placeholder {
      color: var(--input-text);
      opacity: 0.6;
    }
  }
}
.conferenceCards-container {
  background-color: transparent;
  max-width: 74vw;
  margin: 1% 1.8% 1.8%;
  width: 100%;

  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;

  @include media('<=phone') {
    max-width: 100%;
  }
}

.foyerCategory-grided-container {
  background-color: transparent;
  margin: 0;
  width: 100%;
  max-width: 1500px;
  padding: 0 2% 100px;

  justify-content: center;
  align-items: stretch;

  display: grid;
  gap: 1.5%;

  @include media('>=tablet') {
    grid-template-columns: repeat(4, 1fr);

    & > *:nth-child(2) {
      grid-column: 2 / span 2;
      grid-row: 1 / span 2;
    }
  }

  @include media('<=tablet') {
    grid-template-columns: repeat(3, 1fr);

    & > *:nth-child(2) {
      grid-column: 2 / span 2;
    }

    & > *:nth-child(3) {
      grid-column: 1 / span 2;
    }
  }

  @include media('<=phone') {
    padding: 0;
    gap: 0.5rem;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, 1fr);
    & > *:nth-child(1) {
      grid-column: 1 / span 2;
      // double height
      grid-row: 1 / span 2;
    }
    & > *:nth-child(2) {
      grid-column: 1 / span 1;
    }
    & > *:nth-child(3) {
      grid-column: 2 / span 1;
    }
    & > *:nth-child(6) {
      grid-column: 1 / span 2;
    }
  }
}
</style>
